import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { adjustElementPosition, calculateRelativePosition } from '../utils/positionUtils'
import { UI_CONSTANTS } from '../constants'
import { useDelayedAction } from './useDelayedAction'

export interface MessageHoverUiOptions {
  /** Резолвит стабильный store-id сообщения (пикер фиксирует его при открытии). */
  resolveMessageId: (messageId: string | number) => string | number | null
}

/**
 * Hover-состояние сообщений ChatLog: reaction-пикер и контекстное меню
 * (тайминги скрытия, позиционирование, закрытие по клику снаружи).
 */
export function useMessageHoverUi(options: MessageHoverUiOptions) {
  const { resolveMessageId } = options

  const hoveredMessageId = ref<string | number | null>(null)
  const hoveredTriggerId = ref<string | number | null>(null)
  const hoveredPickerId = ref<string | number | null>(null)
  const showPickerId = ref<string | number | null>(null)
  const pickerPosition = ref<{ top: number; left: number } | null>(null)

  const showMenuId = ref<string | number | null>(null)
  const menuPosition = ref<{ top: number; left: number } | null>(null)

  const { schedule: scheduleHide, cancel: cancelHide } = useDelayedAction(
    UI_CONSTANTS.REACTION_PICKER.HIDE_DELAY_MS,
  )

  const { schedule: scheduleTriggerHide, cancel: cancelTriggerHide }
    = useDelayedAction(UI_CONSTANTS.REACTION_PICKER.TRIGGER_HIDE_DELAY_MS)

  const handleTriggerHide = () => {
    cancelTriggerHide()
    scheduleTriggerHide(() => {
      if (!hoveredPickerId.value && !hoveredTriggerId.value) {
        hoveredMessageId.value = null
        scheduleHide(() => {
          if (
            !hoveredPickerId.value
            && !hoveredTriggerId.value
            && !hoveredMessageId.value
          ) {
            showPickerId.value = null
            pickerPosition.value = null
          }
        })
      }
    })
  }

  const onMessageEnter = (messageId: string | number) => {
    cancelTriggerHide()
    hoveredMessageId.value = messageId
    cancelHide()
  }

  const onMessageLeave = (messageId: string | number) => {
    if (hoveredMessageId.value === messageId) handleTriggerHide()
  }

  const closeReactionPicker = () => {
    showPickerId.value = null
    pickerPosition.value = null
    hoveredPickerId.value = null
    hoveredTriggerId.value = null
  }

  const openPicker = (messageId: string | number, event: MouseEvent) => {
    const target = event.currentTarget as HTMLElement
    if (!target) {
      return
    }

    const initialPosition = calculateRelativePosition(target, target)

    pickerPosition.value = initialPosition
    // Фиксируем id на момент открытия — mouseleave/expand не должны терять цель
    showPickerId.value = resolveMessageId(messageId) ?? messageId

    nextTick(() => {
      const pickerEl = document.querySelector('.reaction-picker') as HTMLElement
      if (pickerEl) {
        pickerPosition.value = adjustElementPosition(pickerEl, initialPosition)
      }
    })
  }

  const onTriggerEnter = (messageId: string | number, event: MouseEvent) => {
    cancelTriggerHide()
    hoveredTriggerId.value = messageId
    cancelHide()
    openPicker(messageId, event)
  }

  const onTriggerLeave = (messageId: string | number) => {
    if (hoveredTriggerId.value === messageId) {
      hoveredTriggerId.value = null
      handleTriggerHide()
    }
  }

  const onPickerEnter = () => {
    if (showPickerId.value) {
      hoveredPickerId.value = showPickerId.value
      cancelHide()
      cancelTriggerHide()
    }
  }

  const onPickerLeave = () => {
    hoveredPickerId.value = null

    // Не гасим сразу: клик по эмодзи / expand может вызвать краткий mouseleave
    setTimeout(() => {
      if (!hoveredTriggerId.value && !hoveredPickerId.value && showPickerId.value != null) {
        // Пикер ещё открыт — оставляем, пока не кликнут снаружи или не выберут реакцию
      }
    }, 50)
  }

  const openMessageMenu = (messageId: string | number, event: MouseEvent) => {
    let initialPosition: { left: number; top: number }

    if (event.type === 'contextmenu') {
      initialPosition = {
        left: event.clientX,
        top: event.clientY,
      }
    }
    else {
      const target = event.currentTarget as HTMLElement
      if (!target) return

      const rect = target.getBoundingClientRect()
      initialPosition = {
        left: rect.left + rect.width / 2,
        top: rect.bottom + 8,
      }
    }

    showMenuId.value = messageId

    nextTick(() => {
      const menuEl = document.querySelector('.message-menu') as HTMLElement
      if (menuEl) {
        menuPosition.value = adjustElementPosition(menuEl, initialPosition)
      } else menuPosition.value = initialPosition
    })
  }

  const closeMessageMenu = () => {
    showMenuId.value = null
    menuPosition.value = null
  }

  const onDocumentClick = (e: MouseEvent) => {
    const target = e.target as Node
    const picker = document.querySelector('.reaction-picker')
    const menu = document.querySelector('.message-menu')
    if (picker && !picker.contains(target)) {
      showPickerId.value = null
      pickerPosition.value = null
    }
    if (menu && !menu.contains(target)) {
      showMenuId.value = null
      menuPosition.value = null
    }
  }

  onMounted(() => {
    document.addEventListener('click', onDocumentClick)
  })

  onUnmounted(() => {
    document.removeEventListener('click', onDocumentClick)
    cancelHide()
    cancelTriggerHide()
  })

  return {
    hoveredMessageId,
    showPickerId,
    pickerPosition,
    showMenuId,
    menuPosition,
    onMessageEnter,
    onMessageLeave,
    closeReactionPicker,
    onTriggerEnter,
    onTriggerLeave,
    onPickerEnter,
    onPickerLeave,
    openMessageMenu,
    closeMessageMenu,
  }
}
